import * as Y from 'yjs';

import { BlockRange, type Document, type SelectionRange, TextRange } from '@gitbook/slate';

import { lookupByPath } from './lookup';
import { getYSlateDocument, type YSlateDocument } from './utils';

export type YSelectionRange = {
    anchor?: Y.AbsolutePosition;
    focus?: Y.AbsolutePosition;
};

/**
 * Convert a Slate selection range to a Yjs position.
 */
export function selectionToYSelection(
    document: Document,
    doc: Y.Doc,
    range: SelectionRange
): YSelectionRange {
    const root = getYSlateDocument(doc);

    if (BlockRange.isBlockRange(range)) {
        return {
            anchor: range.anchorBlock
                ? pointToNodeYPosition(root, document, range.anchorBlock)
                : undefined,
            focus: range.focusBlock
                ? pointToNodeYPosition(root, document, range.focusBlock)
                : undefined,
        };
    }
    return {
        anchor: range.anchorKey
            ? pointToYPosition(root, document, range.anchorKey, range.anchorOffset)
            : undefined,
        focus: range.focusKey
            ? pointToYPosition(root, document, range.focusKey, range.focusOffset)
            : undefined,
    };
}

/**
 * Convert a Yjs position to a Slate selection range.
 */
export function ySelectionToSelection(
    document: Document,
    range: YSelectionRange
): SelectionRange | undefined {
    if (!range.anchor || !range.focus) {
        return;
    }

    if (range.anchor.type instanceof Y.XmlText) {
        if (!(range.focus.type instanceof Y.XmlText)) {
            return;
        }

        const anchorKey = range.anchor.type.getAttribute('key');
        const focusKey = range.focus.type.getAttribute('key');

        return TextRange.create({
            anchorKey,
            anchorOffset: range.anchor.index,
            focusKey,
            focusOffset: range.focus.index,
        }).normalize(document);
    }

    if (range.anchor.type instanceof Y.XmlElement) {
        if (!(range.focus.type instanceof Y.XmlElement)) {
            return;
        }

        const anchorKey = range.anchor.type.getAttribute('key');
        const focusKey = range.focus.type.getAttribute('key');

        const anchorParent = document.getDescendant(anchorKey);
        const focusParent = document.getDescendant(focusKey);

        if (!anchorParent || !focusParent || !anchorParent.isBlock() || !focusParent.isBlock()) {
            return;
        }

        const anchorNode = anchorParent.nodes.get(range.anchor.index);
        const focusNode = focusParent.nodes.get(range.focus.index);

        if (!anchorNode || !focusNode) {
            return;
        }

        return BlockRange.create({
            anchorBlock: anchorNode.key,
            focusBlock: focusNode?.key,
        }).normalize(document);
    }
}

function pointToYPosition(
    root: YSlateDocument,
    document: Document,
    key: string,
    index: number
): Y.AbsolutePosition | undefined {
    const path = document.getPath(key);
    if (!path) {
        return;
    }

    const type = lookupByPath(root, path);

    return {
        type,
        index,
        assoc: 0,
    };
}

function pointToNodeYPosition(
    root: YSlateDocument,
    document: Document,
    key: string
): Y.AbsolutePosition | undefined {
    const path = document.getPath(key);
    if (!path) {
        return;
    }

    const type = lookupByPath(root, path.slice(0, -1));

    return {
        type,
        index: path[path.length - 1],
        assoc: 0,
    };
}
